import { UserMenu } from "@/components/auth/user-menu";
import { Button } from "@/components/ui/button";
import { siteMapUrlBuilder } from "@/lib/url-builder";
import { Globe, Menu } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useParams, } from "react-router";
import { useNavigate } from "react-router"

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
}

interface MainLayoutProps {
  navItems: NavItem[];
  siteName: string;
  levelName: string;
}

const MainLayout: React.FC<MainLayoutProps> = ({ navItems, siteName, levelName }) => {
  const navigate = useNavigate();
  const params = useParams();
  const [pageName, setPageName] = useState('Default Page');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    document.title = pageName;
    return () => {
      // Optional: Reset title when component unmounts
      document.title = 'My App';
    };
  }, [pageName]);
  
  return (
     <div className="h-screen flex flex-col overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/')} title="Sites">
              <Globe className="h-4 w-4" />
            </Button>
            <Link to={siteMapUrlBuilder(params.siteId!, null)}>
              <h1 className="text-xl font-bold text-gray-900 hover:text-blue-600 transition-colors duration-200 cursor-pointer">{siteName}</h1>
            </Link>
          </div>

          {/* Hamburger Button for Mobile */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            <Menu />
          </button>

          {/* Desktop Navigation and UserMenu */}
          <div className="hidden md:flex md:items-center md:flex-1">
            <nav className="flex justify-center flex-1">
              <ul className="flex gap-5 list-none items-center">
                <li><strong>{levelName}</strong></li>
                {navItems.map((item) => (
                  <li key={item.label}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        `px-4 py-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors ${
                          isActive ? 'bg-black text-white' : ''
                        }`
                      }
                    >
                      {item.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </nav>
            <div className="w-32">
              <UserMenu />
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden mt-4">
            <ul className="flex flex-col gap-2 list-none">
              {navItems.map((item) => (
                <li key={item.label}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      `block px-4 py-2 rounded-md text-gray-700 hover:bg-gray-100 transition-colors ${
                        isActive ? 'bg-black text-white' : ''
                      }`
                    }
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
              <li className="px-4 py-2">
                <UserMenu />
              </li>
            </ul>
          </div>
        )}
      </div>

      {/* Main content area */}
      <div className="flex flex-1 overflow-y-auto">
        <h2 className="md:hidden">{levelName}</h2>
        
        <Outlet context={{ setPageName }} />
      </div>
    </div>
  );
}

export default MainLayout;